import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import HomeHeader from "@/app/components/common/HomeHeader";
import { inter, philosopher } from "@/lib/fonts";
import {
  defaultPaginationOptions,
  IGetPaginatedOrdersQuery,
  ISellerStatistics,
  IOrder,
  priceFormat,
} from "muminsmart-utils";
import { useAuth } from "@/contexts/AuthContext";
import {
  useGetSellerOrdersQuery,
  useGetSellerStatisticsQuery,
} from "@/lib/api/apiSlice";
import { useShowInitialLoader } from "@/lib/hooks";
import { routes } from "@/lib/constants";
import { cn } from "@/lib/utils";

const STAT_SKELETON_KEYS = [
  "total-sales",
  "listed-items",
  "orders",
  "todays-sales",
] as const;

const ORDER_ROW_SKELETON_KEYS = [
  "order-row-1",
  "order-row-2",
  "order-row-3",
] as const;

type StatCardProps = {
  label: string;
  value: string | number;
  containerClassName: string;
  labelClassName: string;
};

function formatOrderDate(date: Date | string) {
  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "2-digit",
  }).format(new Date(date));
}

function StatCard({
  label,
  value,
  containerClassName,
  labelClassName,
}: Readonly<StatCardProps>) {
  return (
    <div className={cn("rounded-xl p-4", containerClassName)}>
      <p className={cn("text-sm", labelClassName)}>{label}</p>
      <p className={cn("text-2xl font-bold text-secondary", inter.className)}>
        {value}
      </p>
    </div>
  );
}

function StatisticsSkeleton() {
  return STAT_SKELETON_KEYS.map((key) => (
    <div
      key={key}
      className="h-24 animate-pulse rounded-xl bg-[#F0E0C7]/60"
    />
  ));
}

function StatisticsGrid({
  statistics,
}: Readonly<{ statistics?: ISellerStatistics }>) {
  return (
    <>
      <StatCard
        label="Total Sales"
        value={priceFormat(statistics?.totalSales ?? 0)}
        containerClassName="bg-[#F8EEE3]"
        labelClassName="text-[#AA9684]"
      />
      <StatCard
        label="Listed Items"
        value={statistics?.listedItems ?? 0}
        containerClassName="bg-[#DDF2F4]"
        labelClassName="text-[#69C1C9]"
      />
      <StatCard
        label="Orders"
        value={statistics?.returnedOrders ?? 0}
        containerClassName="bg-[#F3F3F3]"
        labelClassName="text-[#BBB1A8]"
      />
      <StatCard
        label="Today's Sales"
        value={priceFormat(statistics?.todaysSales ?? 0)}
        containerClassName="bg-[#F1E9EC]"
        labelClassName="text-[#BBB1A8]"
      />
    </>
  );
}

function OrdersTableSkeleton() {
  return (
    <div className="mt-2 space-y-2">
      {ORDER_ROW_SKELETON_KEYS.map((key) => (
        <div
          key={key}
          className="h-12 animate-pulse rounded-lg bg-[#F0E0C7]/60"
        />
      ))}
    </div>
  );
}

function EmptyOrdersState() {
  return (
    <div className="text-sm text-muted-foreground bg-gray-100 rounded-lg px-4 py-4">
      No recent orders found.
    </div>
  );
}

function OrderRow({
  order,
  index,
}: Readonly<{ order: IOrder; index: number }>) {
  return (
    <div
      className={cn(
        "grid grid-cols-4 text-sm rounded-lg px-4 py-3 mt-2",
        inter.className,
        index % 2 === 0 ? "bg-[#FDFAF7]" : "bg-[#FFFFFF]",
      )}
    >
      <span>{formatOrderDate(order.createdAt)}</span>
      <span className="truncate" title={order.shippingName}>
        {order.shippingName}
      </span>
      <span className="truncate" title={order.shippingCity}>
        {order.shippingCity}
      </span>
      <span>{priceFormat(Number(order.totalAmount))}</span>
    </div>
  );
}

function getRecentOrdersContent(
  showOrdersLoader: boolean,
  recentOrders: IOrder[],
): ReactNode {
  if (showOrdersLoader) {
    return <OrdersTableSkeleton />;
  }

  if (recentOrders.length === 0) {
    return <EmptyOrdersState />;
  }

  return recentOrders.map((order, index) => (
    <OrderRow key={order.id} order={order} index={index} />
  ));
}

export default function SellerDashboardPage() {
  const { data: session, status } = useAuth();

  const { data: ordersData, isLoading: isOrdersLoading } =
    useGetSellerOrdersQuery(
      { ...defaultPaginationOptions } as IGetPaginatedOrdersQuery,
      { skip: status === "loading" },
    );
  const { data: statistics, isLoading: isStatisticsLoading } =
    useGetSellerStatisticsQuery(undefined, { skip: status === "loading" });

  const showOrdersLoader = useShowInitialLoader(isOrdersLoading, ordersData);
  const showStatisticsLoader = useShowInitialLoader(
    isStatisticsLoading,
    statistics,
  );

  const recentOrders = ordersData?.items ?? [];
  const recentOrdersContent = getRecentOrdersContent(
    showOrdersLoader,
    recentOrders,
  );

  return (
    <div className="mx-auto w-full min-h-screen pb-24 py-6">
      <HomeHeader
        primaryText="Welcome,"
        secondaryText={session?.user?.fullName || "User"}
        breakLine
      />

      <div className="grid grid-cols-2 gap-4 px-5 mt-6">
        {showStatisticsLoader ? (
          <StatisticsSkeleton />
        ) : (
          <StatisticsGrid statistics={statistics} />
        )}
      </div>

      <div className="mt-8 bg-white rounded-t-3xl p-5">
        <div
          className={cn(
            "flex justify-between items-center mb-4",
            philosopher.className,
          )}
        >
          <h2 className="font-bold text-teal-900 text-xl">Recent Orders</h2>
          <Link
            to={routes.orders.index}
            className="text-teal-900 underline text-sm"
          >
            View All
          </Link>
        </div>

        <div
          className={cn(
            "bg-teal-900 text-white text-base rounded-lg px-4 py-3 grid grid-cols-4 font-bold",
            philosopher.className,
          )}
        >
          <span>Date</span>
          <span>Order</span>
          <span>Location</span>
          <span>Amount</span>
        </div>

        {recentOrdersContent}
      </div>
    </div>
  );
}
